import React, { useState, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Typography, Snackbar, Alert, CircularProgress } from '@mui/material'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { gruposApi } from '../api/grupos'
import { Grupo, GrupoCreate } from '../types/api'
import GruposTable from '../components/Grupos/GruposTable'
import GrupoForm from '../components/Grupos/GrupoForm'
import DeleteConfirmDialog from '../components/Grupos/DeleteConfirmDialog'
import { createLogger } from '../utils/logger'
import ExportButton from '../components/ExportButton'
import AddButton from '../components/AddButton'
import ActionBar from '../components/ActionBar'

const logger = createLogger('GruposPage')

const GruposPage: React.FC = () => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [formOpen, setFormOpen] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [selectedGrupo, setSelectedGrupo] = useState<Grupo | null>(null)
  const [snackbar, setSnackbar] = useState<{
    open: boolean
    message: string
    severity: 'success' | 'error'
  }>({
    open: false,
    message: '',
    severity: 'success',
  })

  // Buscar grupos
  const {
    data: grupos = [],
    isLoading,
    error,
    isError,
    isSuccess,
  } = useQuery({
    queryKey: ['grupos'],
    queryFn: async () => {
      logger.debug('Iniciando busca de grupos...')
      try {
        const result = await gruposApi.list()
        logger.debug('Grupos recebidos da API:', result.length, 'grupos')
        logger.debug('Tipo do resultado:', typeof result, 'É array?', Array.isArray(result))
        logger.debug('Primeiro grupo:', result[0]?.nome || 'N/A')
        
        // Garantir que retornamos um array
        if (!Array.isArray(result)) {
          logger.error('PROBLEMA: resultado não é array!', result)
          return []
        }
        
        return result
      } catch (err: any) {
        logger.error('Erro ao buscar grupos:', err)
        logger.error('Status:', err.response?.status)
        logger.error('Detalhe:', err.response?.data)
        throw err
      }
    },
    retry: false, // Não tentar novamente em caso de erro 401/403
  })

  // Memoizar colunas do ExportButton para evitar re-renders
  const exportColumns = useMemo(() => [
    { key: 'id', label: 'ID' },
    { key: 'nome', label: 'Nome' },
    { key: 'descricao', label: 'Descrição' },
    { key: 'enforce_limite_acessos', label: 'Enforce Limite', format: (v: any) => v ? 'Sim' : 'Não' },
    { key: 'limite_sessoes_por_usuario', label: 'Limite Sessões' },
    { key: 'permissivo_limite_acessos', label: 'Permissivo', format: (v: any) => v ? 'Sim' : 'Não' },
    { key: 'status', label: 'Status' },
  ], [])

  // Debug: Log dos dados (com dependências corretas)
  useEffect(() => {
    if (isLoading || isError) return // Não logar durante loading ou erro
    logger.debug('Estado da query:', {
      gruposCount: grupos.length,
      gruposType: typeof grupos,
      isArray: Array.isArray(grupos),
      isLoading,
      isError,
      isSuccess,
      error: error ? (error as any).message : null,
      hasError: !!error,
    })
  }, [grupos.length, isLoading, isError, isSuccess, error])

  // Mutação para criar grupo
  const createMutation = useMutation({
    mutationFn: (data: GrupoCreate) => gruposApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['grupos'] })
      setSnackbar({
        open: true,
        message: t('groups.createSuccess'),
        severity: 'success',
      })
    },
    onError: (error: any) => {
      setSnackbar({
        open: true,
        message: error.response?.data?.detail || t('groups.title'),
        severity: 'error',
      })
    },
  })

  // Mutação para atualizar grupo
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<GrupoCreate> }) =>
      gruposApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['grupos'] })
      setSnackbar({
        open: true,
        message: t('groups.updateSuccess'),
        severity: 'success',
      })
    },
    onError: (error: any) => {
      setSnackbar({
        open: true,
        message: error.response?.data?.detail || t('groups.title'),
        severity: 'error',
      })
    },
  })

  // Mutação para deletar grupo
  const deleteMutation = useMutation({
    mutationFn: (id: number) => gruposApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['grupos'] })
      setSnackbar({
        open: true,
        message: t('groups.deactivateSuccess', 'Grupo inativado com sucesso'),
        severity: 'success',
      })
    },
    onError: (error: any) => {
      setSnackbar({
        open: true,
        message: error.response?.data?.detail || t('groups.title'),
        severity: 'error',
      })
    },
  })

  const handleCreate = () => {
    setSelectedGrupo(null)
    setFormOpen(true)
  }

  const handleEdit = (grupo: Grupo) => {
    setSelectedGrupo(grupo)
    setFormOpen(true)
  }

  const handleDelete = (grupo: Grupo) => {
    setSelectedGrupo(grupo)
    setDeleteDialogOpen(true)
  }

  const handleFormSubmit = async (data: GrupoCreate) => {
    if (selectedGrupo) {
      await updateMutation.mutateAsync({ id: selectedGrupo.id, data })
    } else {
      await createMutation.mutateAsync(data)
    }
  }

  const handleDeleteConfirm = async () => {
    if (selectedGrupo) {
      await deleteMutation.mutateAsync(selectedGrupo.id)
    }
  }

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false })
  }

  if (error) {
    const errorMessage = (error as any).response?.data?.detail || (error as Error).message || 'Erro desconhecido'
    const statusCode = (error as any).response?.status
    
    logger.error('Erro exibido:', {
      message: errorMessage,
      status: statusCode,
      error,
    })
    
    return (
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 3,
        }}
      >
        <Alert severity="error">
          <Typography variant="h6">Erro ao carregar grupos</Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            {statusCode === 403 
              ? 'Acesso negado. Você precisa ser Super Administrador ou Administrador para acessar grupos.'
              : statusCode === 401
              ? 'Sessão expirada. Por favor, faça login novamente.'
              : errorMessage}
          </Typography>
          {import.meta.env.DEV && (
            <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
              Status: {statusCode || 'N/A'} | Detalhe: {errorMessage}
            </Typography>
          )}
        </Alert>
      </Box>
    )
  }

  return (
    <Box sx={{ py: 4, px: { xs: 2, md: 3 }, width: '100%', display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
        <ActionBar>
          <ExportButton
            data={Array.isArray(grupos) ? grupos : []}
            columns={exportColumns}
            filename="grupos.xlsx"
            sheetName="Grupos"
          />
          <AddButton onClick={handleCreate} title={t('groups.new')} />
        </ActionBar>

        <Box sx={{ flex: 1, minHeight: 0 }}>
          {isLoading ? (
            <Box p={3} display="flex" justifyContent="center">
              <CircularProgress />
            </Box>
          ) : grupos.length === 0 ? (
            <Box p={3}>
              <Alert severity="info">
                Nenhum grupo encontrado. {grupos.length === 0 && !isLoading && 'Verifique se você tem permissão para ver grupos.'}
              </Alert>
            </Box>
          ) : (
            <GruposTable
              grupos={Array.isArray(grupos) ? grupos : []}
              onEdit={handleEdit}
              onDelete={handleDelete}
              loading={isLoading}
            />
          )}
        </Box>

      <GrupoForm
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onSubmit={handleFormSubmit}
        grupo={selectedGrupo}
        loading={createMutation.isPending || updateMutation.isPending}
      />

      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteConfirm}
        grupo={selectedGrupo}
        loading={deleteMutation.isPending}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default GruposPage
